import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { QRCode } from 'antd'
import copy from 'copy-to-clipboard'
import { attributionFor, buildShareText } from '../share-text'
import { getSiteConfig } from '../constantFunction'
import { assetUrl } from '../tenant'
import { inWechat } from '../wechat-share'

/**
 * 分享面板：这张卡的二维码 + 提示 + 复制链接与署名（+ 手机上的系统分享）。
 * 为什么是二维码：微信规则——从聊天里的纯文本网址点开的页面再转发只会是文字链接；扫码 / 从已有卡片打开再转发才出卡片。
 * 管理员在设置中心「微信分享 → 站内分享按钮」打开后才用它（site.shareQr），否则分享按钮维持只复制文字。
 * 二维码永远放在白底方块上（暗色模式也是），扫码需要浅色静区。
 */
function ShareSheet({ card, open, onClose }) {
    const [copied, setCopied] = useState(false)
    const wechat = inWechat()
    const canSystemShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function'

    useEffect(() => {
        if (!open) return undefined
        const onKey = (e) => {
            if (e.key === 'Escape') onClose()
        }
        document.addEventListener('keydown', onKey)
        const prev = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => {
            document.removeEventListener('keydown', onKey)
            document.body.style.overflow = prev
        }
    }, [open, onClose])

    if (!open || !card) return null

    const a = attributionFor(card)
    const site = getSiteConfig() || {}
    // 站点 SVG logo 自带 prefers-color-scheme 反白：暗色模式下画进白底二维码会变成浅灰，干脆不嵌
    const darkScheme = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
    const logo = site.logoUrl && !darkScheme ? assetUrl(site.logoUrl) : undefined
    const shortUrl = a.url.replace(/^https?:\/\//, '')

    const doCopy = () => {
        copy(buildShareText(card))
        setCopied(true)
        setTimeout(() => setCopied(false), 1400)
    }
    const doSystemShare = () => {
        navigator.share({ title: a.title, text: buildShareText(card), url: a.url }).catch(() => {})
    }

    return createPortal(
        <div className='share-sheet-backdrop' onClick={onClose} role='presentation'>
            <div className='share-sheet' role='dialog' aria-modal='true' aria-label='分享' onClick={(e) => e.stopPropagation()}>
                <div className='share-sheet-head'>
                    <div className='share-sheet-title'>{a.title}</div>
                    <button type='button' className='share-sheet-close' aria-label='关闭' onClick={onClose}>×</button>
                </div>
                <div className='share-sheet-qr'>
                    <QRCode value={a.url} size={176} bordered={false} errorLevel='M' icon={logo} iconSize={36} />
                </div>
                <p className='share-sheet-hint'>
                    {wechat
                        ? <>点右上角 <b>···</b> 即可转发给朋友或分享到朋友圈。<br />如果转出去只是一行链接，<b>长按二维码识别</b>打开本页后再转发。</>
                        : <>用微信<b>扫一扫</b>打开本页，再点右上角 ··· 转发，出去的就是带标题和封面的卡片。</>}
                </p>
                <div className='share-sheet-link'>
                    <span className='share-sheet-url' title={a.url}>{shortUrl}</span>
                    {!canSystemShare && (
                        <button type='button' className='share-sheet-btn' onClick={doCopy}>{copied ? '已复制' : '复制链接与署名'}</button>
                    )}
                </div>
                {canSystemShare && (
                    <div className='share-sheet-actions'>
                        <button type='button' className='share-sheet-btn' onClick={doCopy}>{copied ? '已复制' : '复制链接与署名'}</button>
                        <button type='button' className='share-sheet-btn primary' onClick={doSystemShare}>系统分享…</button>
                    </div>
                )}
            </div>
        </div>,
        document.body,
    )
}

export default ShareSheet
